import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { Outlet, Route, useLocation } from 'react-router-dom'

import { useHeaderNav } from 'context/headerNavContext'
import { usePageTitle } from 'context/pageTitleContext'

import { useSettingsRoutes } from './useSettingsRoutes/useSettingsRoutes'
import DesktopAdditionalContent from '../../../features/DesktopAdditionalContent/DesktopAdditionalContent'
import ProtectedRoute from '../../../routes/ProtectedRoute'

const navUrls = [
  '/settings/general',
  '/settings/account',
  '/settings/notifications'
]

type SettingsRouteComponent =
  | JSX.Element
  | JSX.Element[]
  | React.ComponentType<any>
  | any

const renderComponent = (
  Component: SettingsRouteComponent,
  componentProps?: any
) => {
  return typeof Component === 'function'
    ? <Component {...componentProps} />
    : Component
}

const toSettingsChildPath = (path: string) => {
  return path.replace(/^\/settings\/?/, '')
}

const SettingsLayout = () => {
  const { t } = useTranslation()
  const { setTitle } = usePageTitle()
  const { setHeaderNav } = useHeaderNav()
  const location = useLocation()

  useEffect(() => {
    setHeaderNav(navUrls)

    return () => {
      setHeaderNav([])
    }
  }, [setHeaderNav])

  useEffect(() => {
    const pageTitle =
      location.pathname === '/settings/general/wallet'
        ? 'Wallet'
        : t('settings')

    setTitle(pageTitle)
  }, [location.pathname, setTitle, t])

  return (
    <>
      <Outlet />
      <DesktopAdditionalContent />
    </>
  )
}

export const useRenderSettingsRoutes = () => {
  const settingsRoutes = useSettingsRoutes()

  return (
    <Route path='/settings' element={<SettingsLayout />}>
      {settingsRoutes.flatMap(({ path, Component, roles }) => {
        if (typeof path === 'string') {
          return (
            <Route
              key={path}
              path={toSettingsChildPath(path)}
              element={
                <ProtectedRoute roles={roles ?? []}>
                  {renderComponent(Component)}
                </ProtectedRoute>
              }
            />
          )
        }

        if (Array.isArray(path)) {
          return path.map(item => (
            <Route
              key={item.path}
              path={toSettingsChildPath(item.path)}
              element={
                <ProtectedRoute roles={item.roles ?? []}>
                  {renderComponent(Component, item.props)}
                </ProtectedRoute>
              }
            />
          ))
        }

        return []
      })}
    </Route>
  )
}